import { useState, useEffect, useMemo } from 'react';
import { useDataFetch } from './Use-data-fetch';
import { formatDateDayMonthYear } from '../utils/date';

/**
 * Hook personalizado para obtener y manejar el resumen de settlements
 * @returns {Object} Datos y funciones relacionadas con settlements
 */
export const useSettlementsSummary = () => {
  const { getData } = useDataFetch();
  const [selectedInfluencerPeriod, setSelectedInfluencerPeriod] = useState('current');
  const [selectedLoyaltyPeriod, setSelectedLoyaltyPeriod] = useState('current');
  const [influencerBonus, setInfluencerBonus] = useState(null);
  const [loyaltyRoyalty, setLoyaltyRoyalty] = useState(null);

  // Obtener resumen de settlements
  const { data: settlementsData, isLoading, error, refetch } = getData(
    ['settlements-summary'],
    '/settlements/summary'
  );

  useEffect(() => {
    if (settlementsData?.data) {
      // Separar los datos por tipo
      const influencerData = settlementsData.data.find(item => item.type === 'INFLUENCER_BONUS');
      const loyaltyData = settlementsData.data.find(item => item.type === 'LOYALTY_ROYALTY');

      setInfluencerBonus(influencerData || null);
      setLoyaltyRoyalty(loyaltyData || null);
    }
  }, [settlementsData]);

  // Preparar opciones para el select
  const getPeriodOptions = useMemo(() => {
    const options = [{ value: 'current', label: 'Periodo Actual' }];
    
    if (settlementsData?.data) {
      settlementsData.data.forEach(item => {

        const start = formatDateDayMonthYear(item.period.start);
        const end = formatDateDayMonthYear(item.period.end);
        
        options.push({
          value: item.id,
          label: `${start} - ${end}`,
          data: item
        });
      });
    }
    
    return options;
  }, [settlementsData]);

  // Obtener datos del periodo seleccionado para influencer
  const getSelectedInfluencerData = useMemo(() => {
    if (selectedInfluencerPeriod === 'current') {
      return influencerBonus;
    } else {
      const selectedData = settlementsData?.data?.find(item => item.id === selectedInfluencerPeriod);
      return selectedData?.type === 'INFLUENCER_BONUS' ? selectedData : influencerBonus;
    }
  }, [selectedInfluencerPeriod, influencerBonus, settlementsData]);

  // Obtener datos del periodo seleccionado para loyalty
  const getSelectedLoyaltyData = useMemo(() => {
    if (selectedLoyaltyPeriod === 'current') {
      return loyaltyRoyalty;
    } else {
      const selectedData = settlementsData?.data?.find(item => item.id === selectedLoyaltyPeriod);
      return selectedData?.type === 'LOYALTY_ROYALTY' ? selectedData : loyaltyRoyalty;
    }
  }, [selectedLoyaltyPeriod, loyaltyRoyalty, settlementsData]);

  // Obtener datos combinados para ambos periodos
  const periodData = useMemo(() => {
    return {
      influencer: getSelectedInfluencerData,
      loyalty: getSelectedLoyaltyData
    };
  }, [getSelectedInfluencerData, getSelectedLoyaltyData]);
  
  
  // Obtener información formateada para la interfaz
  const formattedData = useMemo(() => {
    return {
      influencer: getSelectedInfluencerData ? {
        ...getSelectedInfluencerData,
        formattedPeriod: getSelectedInfluencerData?.period ? {
          start: formatDateDayMonthYear(getSelectedInfluencerData.period.start),
          end: formatDateDayMonthYear(getSelectedInfluencerData.period.end)
        } : null,
        formattedCalculationDate: formatDateDayMonthYear(getSelectedInfluencerData?.calculation_date)
      } : null,
      loyalty: getSelectedLoyaltyData ? {
        ...getSelectedLoyaltyData,
        formattedPeriod: getSelectedLoyaltyData?.period ? {
          start: formatDateDayMonthYear(getSelectedLoyaltyData.period.start),
          end: formatDateDayMonthYear(getSelectedLoyaltyData.period.end)
        } : null,
        formattedCalculationDate: formatDateDayMonthYear(getSelectedLoyaltyData?.calculation_date)
      } : null
    };
  }, [getSelectedInfluencerData, getSelectedLoyaltyData]);

  return {
    isLoading,
    error,
    refetch,
    periodOptions: getPeriodOptions,
    selectedInfluencerPeriod,
    setSelectedInfluencerPeriod,
    selectedLoyaltyPeriod,
    setSelectedLoyaltyPeriod,
    periodData,
    formattedData,
    allSettlements: settlementsData?.data || []
  };
};
